import { Card, IconName, Title } from "@introist/react-foundation/v2";
import { ReactNode, useEffect, useState } from "react";
import styled, { css } from "styled-components";

export type TabCardTab = {
  id: string;
  title?: ReactNode;
  startIcon?: IconName;
};

type Props = {
  currentTab?: string;
  tabs: TabCardTab[];
  children?: ReactNode;
  onChange: (tab: string) => void;
};

const StyledTabCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  background-color: var(--palette-surface-subdued);
  border-bottom: 1px solid var(--palette-border-subdued);
`;

const Content = styled.section`
  padding: 1.5rem;
`;

const StyledTabCardTab = styled.div<{ $active: boolean; $isFirst: boolean }>`
  padding: var(--spacing-small) var(--spacing-large);
  z-index: 1;
  position: relative;

  ${({ $active, $isFirst }) => {
    if ($active) {
      return css`
        background-color: white;
        border-left: ${$isFirst ? "none" : "1px solid var(--palette-border-subdued)"};
        border-right: 1px solid var(--palette-border-subdued);

        ::after {
          content: "";
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background-color: white;
        }
      `;
    }
  }}
`;

const Tab = ({
  active,
  tab,
  isFirst,
  onClick
}: {
  active: boolean;
  tab: TabCardTab;
  isFirst: boolean;
  onClick: (id: string) => void;
}) => {
  return (
    <StyledTabCardTab onClick={() => onClick(tab.id)} $active={active} $isFirst={isFirst}>
      <Title variant="bold">{tab.title || tab.id}</Title>
    </StyledTabCardTab>
  );
};

export const TabCard = ({ tabs, currentTab, onChange, children, ...rest }: Props) => {
  const [activeTab, setActiveTab] = useState<string>(currentTab || tabs[0].id);

  useEffect(() => {
    if (!currentTab) return;
    if (currentTab === activeTab) return;

    setActiveTab(currentTab);
  }, [activeTab, currentTab, setActiveTab]);

  return (
    <StyledTabCard {...rest}>
      <Header>
        {tabs.map((tab, idx) => (
          <Tab
            key={`tab-card-tab-${tab.id}`}
            tab={tab}
            isFirst={idx === 0}
            active={tab.id === activeTab}
            onClick={id => {
              setActiveTab(id);
              onChange(id);
            }}
          />
        ))}
      </Header>
      <Content>{children}</Content>
    </StyledTabCard>
  );
};
